import { Heading, HeadingLevel } from "@ariakit/react";
import { MiddotSeparated, QRCode } from "@indietabletop/appkit";
import { type ReactNode } from "react";
import type { Knight } from "../engine/Knight";
import { armorRoll, attackAction, defenseOption, moveAction } from "../fmt";
import {
  getShareLink,
  useEnsureShareId,
} from "../ShareWarbandDialog/useEnsureShareId";
import { KnightContext, useKnight } from "../WarbandPage/KnightContext";
import { DamageLimit, MomentumLimit } from "../WarbandPage/KnightView";
import { useWarbandContext } from "../WarbandPage/useWarbandContext";
import { assertNever } from "./assertNever";
import {
  item,
  knightCard,
  knightItem,
  page,
  printable,
  ruleItem,
} from "./style.css";
import { usePrintContext } from "./usePrintContext";

function KnightSummaryRow(props: { name: string; children: ReactNode }) {
  const { name, children } = props;

  return (
    <div className={item.row.container}>
      <div className={item.row.label}>{name}</div>
      <div className={item.row.value}>{children}</div>
    </div>
  );
}

function KnightCard(props: { knight: Knight }) {
  const { knight } = props;

  return (
    <div key={knight.id} className={knightCard.container}>
      <div className={item.header}>
        <Heading className={knightItem.heading}>
          {knight.name}
          <KnightCount />
        </Heading>

        <div className={item.limits}>
          <DamageLimit value={knight.damageLimit} />
          <MomentumLimit value={knight.momentumLimit} />
        </div>
      </div>
      <div className={knightCard.equipment}>
        <div>
          <strong>Equipment</strong>
        </div>
        <div>{knight.summary}</div>
      </div>
    </div>
  );
}

function KnightCount() {
  const knight = useKnight();
  const print = usePrintContext();

  if (print.layout === "CARDS") {
    return null;
  }

  return (
    knight.count > 1 && <span className={item.count}> x{knight.count}</span>
  );
}

function KnightSection(props: { knight: Knight }) {
  const { knight } = props;

  const print = usePrintContext();

  const usableItems = knight.items
    .filter((lookup) => lookup.isSuccess)
    .map((lookup) => lookup.value);

  return (
    <div key={knight.id} className={knightItem.container}>
      <div className={item.header}>
        <Heading className={knightItem.heading}>
          {knight.name}
          <KnightCount />
        </Heading>

        <div className={knightItem.summary}>{knight.summary}</div>

        {!print.showKnights && (
          <div className={item.limits}>
            <DamageLimit value={knight.damageLimit} />
            <MomentumLimit value={knight.momentumLimit} />
          </div>
        )}
      </div>

      <div className={printable.columns}>
        <KnightSummaryRow name="Attacks">
          <p className={item.row.text}>
            {knight.attackActions.map(renderList(attackAction))}
          </p>

          {knight.attackSpecialRules.map((r, i) => {
            return (
              <p key={i} className={item.row.rule}>
                {r.rule}
              </p>
            );
          })}
        </KnightSummaryRow>

        <KnightSummaryRow name="Defense">
          <p className={item.row.text}>
            {knight.defenseOptions.map(renderList(defenseOption))}
          </p>

          {knight.defenseSpecialRules.map((r, i) => {
            return (
              <p key={i} className={item.row.rule}>
                {r.rule}
              </p>
            );
          })}
        </KnightSummaryRow>

        <KnightSummaryRow name="Armor">
          <p className={item.row.text}>
            {knight.armorRolls.map(renderList(armorRoll))}
          </p>

          {knight.armorSpecialRules.map((r, i) => {
            return (
              <p key={i} className={item.row.rule}>
                {r.rule}
              </p>
            );
          })}
        </KnightSummaryRow>

        <KnightSummaryRow name="Move">
          <p className={item.row.text}>
            {knight.moveActions.map(renderList(moveAction))}
          </p>
        </KnightSummaryRow>

        {!!usableItems.length && (
          <KnightSummaryRow name="Usable">
            {usableItems.map((i) => {
              return (
                <p className={item.row.text} key={i.id}>
                  {i.name}: <em>{i.summary}</em>
                </p>
              );
            })}
          </KnightSummaryRow>
        )}

        <KnightSummaryRow name="Passive">
          {knight.passiveRules.map((r, index) => {
            return (
              <p key={index} className={item.row.rule}>
                {r.rule}
              </p>
            );
          })}
        </KnightSummaryRow>
      </div>

      {print.showKnights && (
        <HeadingLevel>
          <div className={knightItem.instances}>
            {Array.from({ length: knight.count }, (_, index) => {
              return (
                <div key={index}>
                  <Heading className={knightItem.instanceHeading}>
                    Knight {index + 1}
                  </Heading>
                  <div className={item.limits}>
                    <DamageLimit value={knight.damageLimit} />
                    <MomentumLimit value={knight.momentumLimit} />
                  </div>
                </div>
              );
            })}
          </div>
        </HeadingLevel>
      )}
    </div>
  );
}

function renderList<T>(format: (item: T) => string) {
  return (item: T, index: number) => [index !== 0 && ", ", format(item)];
}

function WarbandKnights() {
  const warband = useWarbandContext();
  const print = usePrintContext();

  if (warband.knights.length === 0) {
    return <p className={printable.empty}>This warband has no knights yet.</p>;
  }

  switch (print.layout) {
    case "SUMMARIES": {
      return (
        <div className={printable.summaries}>
          {warband.knights.map((knight) => {
            return (
              <KnightContext key={knight.id} value={knight}>
                <KnightSection knight={knight} />
              </KnightContext>
            );
          })}
        </div>
      );
    }

    case "CARDS": {
      const knights = warband.knights.flatMap((knight) => {
        return Array.from({ length: knight.count }, (_, index) => ({
          key: `${knight.id}:${index}`,
          knight,
        }));
      });

      return (
        <div className={printable.cards}>
          {knights.map(({ key, knight }) => {
            return (
              <KnightContext key={key} value={knight}>
                <KnightCard knight={knight} />
              </KnightContext>
            );
          })}
        </div>
      );
    }

    default: {
      assertNever(print.layout);
    }
  }
}

function RuleReference() {
  const print = usePrintContext();
  const warband = useWarbandContext();

  if (print.layout === "SUMMARIES") {
    return null;
  }

  return (
    <div className={printable.reference}>
      <Heading className={page.heading}>Rules Reference</Heading>

      <HeadingLevel>
        <div className={printable.columns}>
          {warband.equipment.map((equipment) => {
            return (
              <div key={equipment.id} className={ruleItem.container}>
                <Heading className={page.heading}>{equipment.name}</Heading>
                <p>{equipment.summary}</p>
              </div>
            );
          })}
        </div>
      </HeadingLevel>
    </div>
  );
}

function WarbandHeader() {
  const warband = useWarbandContext();
  const shareLink = useEnsureShareId(warband).mapSuccess((id) =>
    getShareLink(id),
  );

  return (
    <div className={printable.header}>
      <div>
        <Heading className={printable.title}>{warband.name}</Heading>
        <MiddotSeparated className={printable.summary}>
          <em>{warband.buildType}</em>
          <>Greathelm {warband.ruleset.version}</>
        </MiddotSeparated>
      </div>

      <div className={printable.qr}>
        <QRCode url={shareLink} />
        <div>Scan to load in the app</div>
      </div>
    </div>
  );
}

export function WarbandPrintable() {
  return (
    <div className={printable.container}>
      <div className={printable.paper}>
        <WarbandHeader />

        <HeadingLevel>
          <WarbandKnights />

          <RuleReference />
        </HeadingLevel>
      </div>
    </div>
  );
}
